import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useProvideFullBleed } from '../shell/fullBleed';
import { useTheme } from '../design/useTheme';
import type { Status } from '../plan/arc';
import { NookThumb, TintedNookGlyph } from '../workspaces/NookCanvas';
import type { Workspace } from '../workspaces/workspace';
import { StatusGlyph } from '../board/StatusGlyph';
import { useFolders } from '../folders/FolderChip';
import { markWhereDiffers, TagCrumb, TagMarkGlyph, tagMarkOf } from '../folders/TagMark';
import type { TagMark } from '../folders/TagMark';
import './doc.css';

/**
 * A page without a plane: the document sheet a recording opens into on a
 * nook, grown to a page. One anatomy for every kind — a head saying what it is
 * and where it stands, a reading column with nothing beside it, where it
 * belongs in a margin on the right, and its secondary views as quiet switches
 * between the head and the long body. The page owns the window; the head's
 * own header steps away, as it does for a nook.
 *
 * The page's own tag is a crumb over the title. A row in the margin ends in
 * its item's mark only where that item's tag is not the page's.
 */
export function DocPage({
  fallback,
  actions,
  menu = [],
  children,
  margin,
  writing,
  gaveWay = false,
  footnote,
  ground,
  tags = [],
}: {
  /** Where back goes when this page was the first one opened. */
  fallback: string;
  /** The page's own action, beside ⋯ — an arc's Write. */
  actions?: ReactNode;
  /** What ⋯ offers beyond the theme. */
  menu?: { label: string; run: () => void }[];
  /** The column: head, switches, then the body. */
  children: ReactNode;
  margin: MarginGroup[];
  /** Writing docked to the right, where the column slides aside for it. */
  writing?: ReactNode;
  gaveWay?: boolean;
  /** Tertiary, at the foot of the margin — when the thing was made. */
  footnote?: string;
  /** The page's tag's ground, where it has one. */
  ground?: string;
  /** The item's own tags: its crumb, and what the margin's marks differ from. */
  tags?: readonly string[];
}) {
  useProvideFullBleed();
  const navigate = useNavigate();
  const location = useLocation();
  const folders = useFolders();
  const { theme } = useTheme();
  const shown = margin.filter((g) => g.rows.length > 0 || g.add);
  const own = tagMarkOf(tags, folders, theme);
  const markOf = (row: MarginRow) =>
    markWhereDiffers(tagMarkOf(row.tags ?? [], folders, theme), own);

  return (
    <div className={`doc${gaveWay ? ' doc-gave-way' : ''}`} style={ground ? { background: ground } : undefined}>
      <div className="doc-top">
        <button
          type="button"
          className="doc-btn doc-icon"
          aria-label="Back"
          // A page opened straight from a link has nothing behind it in this
          // tab; back then goes to the place it belongs to.
          onClick={() => (location.key === 'default' ? navigate(fallback) : navigate(-1))}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <span className="doc-top-spacer" />
        {actions}
        <DocMenu items={menu} />
      </div>

      <div className="doc-main">
        <article className="doc-column">
          {own && <TagCrumb mark={own} className="doc-crumb" />}
          {children}
        </article>
        {!gaveWay && (shown.length > 0 || footnote) && (
          <aside className="doc-margin">
            {shown.map((group) => (
              <section key={group.label} className="doc-group">
                <h2 className="doc-group-label">{group.label}</h2>
                {group.rows.map((row) => (
                  <MarginRowView key={row.key} row={row} mark={markOf(row)} />
                ))}
                {group.add && (
                  <button type="button" className="doc-add" onClick={group.add.onClick}>
                    {group.add.label}
                  </button>
                )}
              </section>
            ))}
            {footnote && <p className="doc-footnote">{footnote}</p>}
          </aside>
        )}
      </div>

      {writing}
    </div>
  );
}

export type MarginGlyph =
  | { kind: 'status'; status: Status }
  | { kind: 'nook'; tags?: readonly string[] }
  | { kind: 'recording' }
  | { kind: 'note' }
  | { kind: 'none' };

export interface MarginRow {
  key: string;
  label: string;
  /** A quiet word after the label — a storyline's "N inside". */
  meta?: string;
  glyph: MarginGlyph;
  /** A route to go to, or an act to perform — opening a note in writing. */
  to?: string;
  onOpen?: () => void;
  /** A nook nobody named, shown as its canvas where the label would be. */
  unnamedNook?: Workspace;
  /** The named item's tags, for its mark. */
  tags?: readonly string[];
  /** What a ⋮ on the hovered row offers. */
  menu?: { label: string; run: () => void }[];
}

export interface MarginGroup {
  label: string;
  rows: MarginRow[];
  /** A quiet text button under the rows, which keeps the group on the page
   *  while it holds nothing. */
  add?: { label: string; onClick: () => void };
}

function MarginRowView({ row, mark }: { row: MarginRow; mark: TagMark | null }) {
  const inner = (
    <>
      <span className="doc-row-glyph">
        <Glyph glyph={row.glyph} />
      </span>
      <span className="doc-row-label">
        {row.unnamedNook ? <NookThumb nook={row.unnamedNook} /> : row.label}
        {row.meta && <span className="doc-row-meta">{row.meta}</span>}
      </span>
      {mark && (
        <span className="doc-row-mark">
          <TagMarkGlyph mark={mark} size={12} />
        </span>
      )}
    </>
  );
  const body = row.to ? (
    <Link to={row.to} className="doc-row">
      {inner}
    </Link>
  ) : (
    <button type="button" className="doc-row" onClick={row.onOpen} disabled={!row.onOpen}>
      {inner}
    </button>
  );
  if (!row.menu || row.menu.length === 0) return body;
  return (
    <div className="doc-row-wrap">
      {body}
      <RowMore items={row.menu} />
    </div>
  );
}

function RowMore({ items }: { items: { label: string; run: () => void }[] }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest('.doc-row-more')) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <span className="doc-row-more">
      <button type="button" className="doc-row-dots" aria-label="More" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="1.7" />
          <circle cx="12" cy="12" r="1.7" />
          <circle cx="19" cy="12" r="1.7" />
        </svg>
      </button>
      {open && (
        <div className="head-menu" role="menu">
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              onClick={() => {
                item.run();
                setOpen(false);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function Glyph({ glyph }: { glyph: MarginGlyph }) {
  if (glyph.kind === 'status') return <StatusGlyph status={glyph.status} size={9} />;
  if (glyph.kind === 'none') return null;
  if (glyph.kind === 'nook') return <TintedNookGlyph tags={glyph.tags} />;
  return <span className={`doc-glyph doc-glyph-${glyph.kind}`} />;
}

/**
 * Secondary views, as quiet words rather than tabs: the open one in ink, the
 * others with a small ⌄. They sit above the long body rather than after it,
 * so reaching one never means scrolling past the whole of another. A switch
 * can also be a way somewhere — an arc's plane — which goes rather than opens.
 */
export function Switches<K extends string>({
  options,
  open,
  onOpen,
}: {
  options: { key: K; label: string; go?: () => void }[];
  open: K | null;
  onOpen: (key: K | null) => void;
}) {
  if (options.length === 0) return null;
  return (
    <div className="doc-switches" role="tablist">
      {options.map((option) => (
        <button
          key={option.key}
          type="button"
          role="tab"
          aria-selected={open === option.key}
          className="doc-switch"
          onClick={() => (option.go ? option.go() : onOpen(open === option.key ? null : option.key))}
        >
          {option.label}
          {open !== option.key && (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          )}
        </button>
      ))}
    </div>
  );
}

/** The quiet lead-word lines under an arc's name: "Start with — …". */
export function HeadLine({ lead, children }: { lead: string; children: ReactNode }) {
  return (
    <p className="doc-line">
      <span className="doc-lead">{lead}</span> — {children}
    </p>
  );
}

function DocMenu({ items }: { items: { label: string; run: () => void }[] }) {
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest('.doc-more')) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const all = [...items, { label: theme === 'dark' ? 'Light theme' : 'Dark theme', run: toggle }];

  return (
    <span className="doc-more">
      <button
        type="button"
        className="doc-btn doc-icon"
        aria-label="More"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="1.7" />
          <circle cx="12" cy="12" r="1.7" />
          <circle cx="19" cy="12" r="1.7" />
        </svg>
      </button>
      {open && (
        <div className="head-menu" role="menu">
          {all.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              onClick={() => {
                item.run();
                setOpen(false);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
